import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useRecoilValue } from "recoil";
import { useSnackbar } from "notistack";
import { authState } from "../../Recoil/RecoilState";
import { fetchFoundItems, fetchLostFoundCameras, setFoundItemStatus } from "../../services/lostFoundApi";
import {
  FOUND_STATUSES,
  FOUND_STATUS_COLOR,
  ITEM_COLORS,
  OBJECT_CLASSES,
  errorText,
  formatDateTime,
  titleCase,
} from "./lostFoundConstants";

const PER_PAGE = 24;

/** One-click ranges; the From/To fields stay editable afterwards. */
const QUICK_RANGES = [
  { label: "Last hour", from: () => dayjs().subtract(1, "hour") },
  { label: "Today", from: () => dayjs().startOf("day") },
  { label: "Last 24 h", from: () => dayjs().subtract(24, "hour") },
  { label: "Last 7 days", from: () => dayjs().subtract(7, "day") },
];

const EMPTY_FILTERS = { camera_id: "", class_name: "", color: "", status: "", start: "", end: "" };

/** datetime-local value (browser time, no zone) <-> ISO string for the API. */
const toLocalInput = (value) => (value ? dayjs(value).format("YYYY-MM-DDTHH:mm") : "");
const fromLocalInput = (value) => (value ? dayjs(value).toISOString() : "");

/**
 * The found gallery: every abandoned object, searchable by camera, date/time, object
 * type, colour and status. Plain indexed filters, so it answers instantly.
 */
const FoundItemsGallery = () => {
  const { token } = useRecoilValue(authState);
  const { enqueueSnackbar } = useSnackbar();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [evidence, setEvidence] = useState(null);

  const rangeInvalid = Boolean(filters.start && filters.end && dayjs(filters.start).isAfter(dayjs(filters.end)));
  const hasFilters = Object.values(filters).some(Boolean);

  useEffect(() => {
    fetchLostFoundCameras(token, false)
      .then((list) => setCameras(Array.isArray(list) ? list : []))
      .catch(() => setCameras([]));
  }, [token]);

  const load = useCallback(async () => {
    if (rangeInvalid) return;
    setLoading(true);
    setError(null);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      setData(await fetchFoundItems({ ...params, page, per_page: PER_PAGE }, token));
    } catch (err) {
      setError(errorText(err, "Could not load the found gallery."));
    } finally {
      setLoading(false);
    }
  }, [filters, page, token, rangeInvalid]);

  useEffect(() => {
    if (token) load();
  }, [load, token]);

  const setFilter = (updates) => {
    setFilters((current) => ({ ...current, ...updates }));
    setPage(1);
  };

  const changeStatus = async (item, status) => {
    try {
      await setFoundItemStatus(item.found_id, status, token);
      enqueueSnackbar(`Item marked ${titleCase(status).toLowerCase()}.`, { variant: "success" });
      load();
    } catch (err) {
      enqueueSnackbar(errorText(err), { variant: "error" });
    }
  };

  const items = data?.items || [];
  const total = data?.total ?? 0;

  return (
    <Box>
      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Camera" size="small" fullWidth value={filters.camera_id}
              onChange={(e) => setFilter({ camera_id: e.target.value })}>
              <MenuItem value="">All cameras</MenuItem>
              {cameras.map((cam) => (
                <MenuItem key={cam.stream_id} value={cam.stream_id}>
                  {cam.camera_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField type="datetime-local" label="From" size="small" fullWidth value={toLocalInput(filters.start)}
              onChange={(e) => setFilter({ start: fromLocalInput(e.target.value) })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField type="datetime-local" label="To" size="small" fullWidth value={toLocalInput(filters.end)}
              onChange={(e) => setFilter({ end: fromLocalInput(e.target.value) })} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Object type" size="small" fullWidth value={filters.class_name}
              onChange={(e) => setFilter({ class_name: e.target.value })}>
              <MenuItem value="">Any type</MenuItem>
              {OBJECT_CLASSES.map((value) => (
                <MenuItem key={value} value={value}>{titleCase(value)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Colour" size="small" fullWidth value={filters.color}
              onChange={(e) => setFilter({ color: e.target.value })}>
              <MenuItem value="">Any colour</MenuItem>
              {ITEM_COLORS.map((value) => (
                <MenuItem key={value} value={value}>{titleCase(value)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Status" size="small" fullWidth value={filters.status}
              onChange={(e) => setFilter({ status: e.target.value })}>
              <MenuItem value="">All statuses</MenuItem>
              {FOUND_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>{titleCase(value)}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
          {QUICK_RANGES.map((range) => (
            <Chip key={range.label} label={range.label} size="small" variant="outlined" clickable
              onClick={() => setFilter({ start: range.from().toISOString(), end: "" })} />
          ))}
          <Box sx={{ flexGrow: 1 }} />
          {hasFilters && (
            <Button size="small" startIcon={<ClearIcon />} onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}>
              Clear filters
            </Button>
          )}
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        </Stack>
      </Card>

      {rangeInvalid && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          &quot;From&quot; is after &quot;To&quot; — adjust the range to search.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>{total}</strong> {total === 1 ? "item" : "items"} found
        </Typography>
      )}

      {loading && !data ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : items.length ? (
        <Grid container spacing={2} sx={{ opacity: loading ? 0.6 : 1 }}>
          {items.map((item) => (
            <Grid item key={item.found_id} xs={12} sm={6} md={4} lg={3}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }} data-testid="found-item">
                {item.crop_path ? (
                  <CardMedia component="img" height="180" image={item.crop_path} alt={`Found ${item.class_name}`}
                    sx={{ objectFit: "contain", bgcolor: "#000" }} />
                ) : (
                  <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "action.hover" }}>
                    <Typography variant="caption" color="text.secondary">No crop stored</Typography>
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">{titleCase(item.class_name)}</Typography>
                    <Chip size="small" label={titleCase(item.status)} color={FOUND_STATUS_COLOR[item.status] || "default"} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                    <VideocamIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">{item.camera_name || "Camera deleted"}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">{formatDateTime(item.found_at)}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                    {item.dominant_color && <Chip size="small" variant="outlined" label={titleCase(item.dominant_color)} />}
                    {(item.snapshot_path || item.video_path) && (
                      <Chip size="small" variant="outlined" clickable label="View evidence" onClick={() => setEvidence(item)} />
                    )}
                    {item.status === "unclaimed" && (
                      <Chip size="small" variant="outlined" clickable label="Discard" onClick={() => changeStatus(item, "discarded")} />
                    )}
                    {item.status === "discarded" && (
                      <Chip size="small" variant="outlined" clickable label="Restore" onClick={() => changeStatus(item, "unclaimed")} />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card sx={{ p: 6, textAlign: "center" }}>
          <ImageSearchIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            {hasFilters ? "Nothing matches these filters" : "The gallery is empty"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasFilters
              ? "Widen the time range, or pick another camera, type or colour."
              : "Items land here automatically whenever a camera raises an abandoned-object alert."}
          </Typography>
        </Card>
      )}

      {total > PER_PAGE && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination count={Math.ceil(total / PER_PAGE)} page={page} color="primary" onChange={(_, value) => setPage(value)} />
        </Box>
      )}

      <Dialog open={Boolean(evidence)} onClose={() => setEvidence(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {evidence && `${titleCase(evidence.class_name)} — ${evidence.camera_name || "Camera"}`}
        </DialogTitle>
        <DialogContent>
          {evidence?.video_path ? (
            <video controls width="100%" poster={evidence.snapshot_path || undefined} style={{ display: "block", backgroundColor: "#000" }}>
              <source src={evidence.video_path} type="video/mp4" />
            </video>
          ) : evidence?.snapshot_path ? (
            <img src={evidence.snapshot_path} alt="Alert frame" style={{ width: "100%", display: "block" }} />
          ) : null}
          {evidence && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Found {formatDateTime(evidence.found_at)}
              {evidence.confidence != null && ` · detection confidence ${(evidence.confidence * 100).toFixed(0)}%`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidence(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FoundItemsGallery;
